<template>
	<v-card>
		<v-toolbar flat card>
			<v-radio-group v-model="showing" row hide-details>
				<v-radio label="Show only expiring soon and expired" value="expiring" />
				<v-radio label="Show all" value="all" />
			</v-radio-group>
			<v-spacer />
			<v-text-field v-model="searchInput" placeholder="Search" prepend-inner-icon="search" hide-details class="mb-2" />
			<v-btn v-if="$_hasAccessToFeature($FeatureId.CERTIFICATIONS_MANAGE, $PermissionAction.USE) || loggedInEmployeeIsAdminOrManager" small @click="$emit('onUploadNewClick')" color="primary" class="ml-2">Add Certification</v-btn>
		</v-toolbar>

		<v-card-text class="text-xs-left">
			<v-data-table :headers="headers" :items="certificationsForTable" :pagination.sync="pagination" :search="searchInput" disable-initial-sort class="certs-table">
				<template v-slot:items="props">
					<tr :class="getRowHighlightClass(props.item)">
						<td>
							<router-link v-if="$_hasAccessToEmployee(loggedInEmployee, props.item.employeeId )" :to="{ name: 'employee', params: { id: props.item.employeeId } }">
								{{ props.item.employeeName }}
							</router-link>
							<span v-else>
								{{ props.item.employeeName }}
							</span>
						</td>
						<td><strong><a href="#" @click.prevent.stop="handleOpenCertificationClick(props.item)">{{ props.item.name }}</a></strong></td>
						<td>
							<v-chip v-if="getStatusLabel(props.item.expiryDate)" :color="getStatusColor(props.item.expiryDate)" small dark>
								{{ getStatusLabel(props.item.expiryDate) }}
							</v-chip>
							<span v-else>—</span>
						</td>
						<td>
							<span>
								{{ props.item.expiryDate ? moment(props.item.expiryDate).fromNow() : '--' }}
							</span>
						</td>
						<td><v-icon v-if="props.item.lastReminder" small class="mr-1">mdi-email-fast-outline</v-icon>{{ props.item.lastReminder ? props.item.lastReminder.fromNow() : '--' }}</td>
						<td>{{ props.item.nextReminder ? props.item.nextReminder.fromNow() : '--' }}</td>
						<td style="padding-left: 0; padding-right: 0;">
							<v-btn v-if="isExpired(props.item.expiryDate)" color="error" flat type="text" small @click="handleUpdateClick(props.item.certification)">Update</v-btn>
						</td>
						<td style="text-align: right; padding-left: 0; padding-right: 0;">
							
							<v-menu offset-y v-if="shouldShowActionMenu(props.item.certification)" :close-on-content-click="false">
								<template v-slot:activator="{ on }">
									<v-btn icon v-on="on"><v-icon>mdi-dots-horizontal</v-icon></v-btn>
								</template>
								<certification-action-menu :certification="props.item.certification" />
							</v-menu>
						</td>
					</tr>
				</template>
			</v-data-table>
		</v-card-text>

		<certification-action-menu-handlers />
		<certification-dialog v-if="showCertificationDialog" :certification="editingCertification" @closeDialog="showCertificationDialog = false" @onCertificationUpdated="onCertificationUpdated"/>
	</v-card>
</template>

<script>
	import { mapActions, mapGetters } from 'vuex';
	import CertificationActionMenu from '@components/certifications/CertificationActionMenu.vue';
	import CertificationActionMenuHandlers from '@components/certifications/CertificationActionMenuHandlers.vue';
	import CertificationDialog from '@components/certifications/CertificationDialog.vue';
	import certificationsMixin from '@/mixins/certificationsMixin';
	import moment from 'moment';

	export default {
		name: 'CertificationsTable',
		mixins: [certificationsMixin],
		components: { CertificationActionMenuHandlers, CertificationActionMenu, CertificationDialog },
		props: {
			enableEditing: {
				default: false
			}
		},
		data() {
			return {
				showing: 'all',
				pagination: {
					rowsPerPage: 10
				},
				headers: [
					{ text: this.$term('employee', { case: 'title' }), value: 'employeeName' },
					{ text: 'Certification', value: 'name' },
					{ text: 'Status', value: 'status' },
					{ text: 'Expiry', value: 'expiryDate' },
					{ text: 'Last Reminder', value: 'lastReminder' },
					{ text: 'Next Reminder', value: 'nextReminder' },
					{ text: '', value: '', sortable: false }
				],
				searchInput: null,
				showCertificationDialog: false,
				editingCertification: undefined
			};
		},
		computed: {
			...mapGetters({
				certifications: 'certifications/getCertifications',
				certificationsExpiringSoon: 'certifications/expiringSoon',
				certificationsExpired: 'certifications/expired',
				loggedInEmployeeIsAdminOrManager: 'loggedInEmployeeIsAdminOrManager',
				loggedInEmployee: 'loggedInEmployee'
			}),
			certificationsToShow() {
				if (this.showing === 'expiring') {
					return [...this.certificationsExpiringSoon, ...this.certificationsExpired];
				}
				return this.certifications || [];
			},
			certificationsForTable() {
				return this.certificationsToShow
					.map(x => ({
						employeeId: x.employeeId,
						employeeName: x.employeeName,
						name: x.name,
						expiryDate: x.expiryDate,
						lastReminder: x.lastReminderUtc ? moment(x.lastReminderUtc) : null,
						nextReminder: x.nextReminderUtc && moment(x.nextReminderUtc).isAfter(moment()) ? moment(x.nextReminderUtc) : null,
						certification: x
					}))
					.sort((a, b) => {
						// Push items without expiry to the bottom
						if (!a.expiryDate && b.expiryDate) return 1;
						if (a.expiryDate && !b.expiryDate) return -1;

						// If both have expiry, sort by date descending
						if (a.expiryDate && b.expiryDate) {
							return new Date(a.expiryDate) - new Date(b.expiryDate);
						}

						// If neither has expiry, keep original order
						return 0;
					});
			}
		},
		methods: {
			...mapActions({
				sendReminder: 'certifications/sendReminder'
			}),
			getRowHighlightClass(item) {
				const { expiryDate } = item;
				if (this.isExpired(expiryDate)) return 'table-row-expired';
				if (this.isExpiring(expiryDate)) return 'table-row-expiring';
				return '';
			},
			shouldShowActionMenu() {
				return this.loggedInEmployeeIsAdminOrManager;
			},
			handleUploadClick(cert) {
				this.editingCertTypeId = cert.certificationTypeId;
				this.isShowingCertUploadDialog = true;
			},
			handleUpdateClick(cert) {
				this.$emit('onUpdateClick', cert);
			},
			handleOpenCertificationClick(cert) {
				this.showCertificationDialog = true;
				this.editingCertification = cert.certification;
			},
			onCertificationUpdated(updatedCertification) {
				this.editingCertification = updatedCertification;
			}
		}
	};
</script>

<style>
	.certs-table .table-row-expired {
		background-color: #ffeceb !important;
	}
	.certs-table .table-row-expiring {
		background-color: #fff8e1 !important;
	}
	.certs-table .table-row-expired td:first-child {
		border-left: 4px solid #ff5722 !important;
	}
	.certs-table .table-row-expiring td:first-child {
		border-left: 4px solid #ffa000 !important;
	}
</style>
